import { Outlet, useLocation } from 'react-router-dom';
import { PopupWidget } from 'react-calendly';
import { useEffect } from 'react';
import useCreateErrorReport from './hooks/use-create-error-report';
import reportError from './utils/helpers/error-report';

declare global {
  interface Window {
    __hmsEarlyErrors?: unknown[];
    __hmsEFPLog?: Record<string, unknown>[];
  }
}

const App = () => {
  const location = useLocation();

  const createErrorReport = useCreateErrorReport();

  useEffect(() => {
    // Flush any early errors captured before React mounted
    const early: unknown[] = window.__hmsEarlyErrors ?? [];

    if (Array.isArray(early) && early.length) {
      early.forEach((entry) => {
        const payload = entry as { type: string; [key: string]: unknown };

        if (payload && typeof payload.type === 'string') {
          reportError(payload, createErrorReport.mutate);
        }
      });
      window.__hmsEarlyErrors = [];
    }
  }, []);

  useEffect(() => {
    const prevOnerror = window.onerror;

    window.onerror = function (message, source, lineno, colno, error) {
      // Ignore errors originating from browser extensions
      if (
        typeof source === 'string' &&
        source.startsWith('chrome-extension://')
      ) {
        return true;
      }

      reportError(
        {
          type: 'public_global_error',
          message,
          source,
          lineno,
          colno,
          stack: error?.stack,
          url: window.location.href,
          referrer: document.referrer,
          viewport: {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
          },
          scroll: { x: window.scrollX, y: window.scrollY },
          visibility: document.visibilityState,
          elementFromPointLog: window.__hmsEFPLog,
          userAgent: navigator.userAgent,
          ts: new Date().toISOString(),
        },
        createErrorReport.mutate
      );
      return true;
    };

    const prevOnunhandledrejection = window.onunhandledrejection;

    window.onunhandledrejection = function (event) {
      const reasonMessage =
        typeof event.reason === 'string'
          ? event.reason
          : (event.reason?.message ?? String(event.reason));

      // Ignore a known false positive emitted by browser extensions'
      // injected content scripts, not by our code.
      if (
        /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(
          reasonMessage
        )
      ) {
        event.preventDefault();
        return;
      }

      reportError(
        {
          type: 'public_unhandled_rejection',
          reasonMessage,
          reasonStack:
            typeof event.reason?.stack === 'string'
              ? event.reason.stack
              : undefined,
          url: window.location.href,
          userAgent: navigator.userAgent,
          ts: new Date().toISOString(),
        },
        createErrorReport.mutate
      );
      event.preventDefault();
    };

    return () => {
      window.onerror = prevOnerror;
      window.onunhandledrejection = prevOnunhandledrejection;
    };
  }, [createErrorReport.mutate]);

  const showPopup =
    !location.pathname.includes('/registration') &&
    !location.pathname.includes('/pro/verify-company-info');

  return (
    <>
      <Outlet />
      {showPopup && (
        <PopupWidget
          url='https://calendly.com/hiremysub-sales/30min'
          rootElement={document.getElementById('calendly-root') as HTMLElement}
          text='Book Demo'
          textColor='#ffffff'
          color='#06B6D4'
        />
      )}
    </>
  );
};

export default App;
